import type {
    AiTradePlanOverlay,
    AiTradeSession,
    NeutralTradeSession,
    PatternPageAiState,
    SignalTradeDraft,
} from '../types';
import {toPosSide, validatePriceRelation} from '../utils/tradeDirection';

export function selectCapabilities(state: PatternPageAiState) {
    return state.capabilities;
}

export function selectAnalysisByKey(state: PatternPageAiState) {
    return state.analysisByKey;
}

export function selectExpandedAnalysis(state: PatternPageAiState) {
    if (null == state.expandedAnalysisKey) return null;
    return state.analysisByKey[state.expandedAnalysisKey] ?? null;
}

export function selectExpandedKey(state: PatternPageAiState) {
    return state.expandedAnalysisKey;
}

export function selectOverlayVisible(state: PatternPageAiState) {
    return state.overlayVisible;
}

export function selectTradeSession(state: PatternPageAiState) {
    return state.tradeSession;
}

export function selectSubmissionSnapshot(state: PatternPageAiState) {
    return state.submissionSnapshot;
}

export function selectSubmissionStatus(state: PatternPageAiState) {
    return state.submissionStatus;
}

function selectSessionDraft(
    session: AiTradeSession | NeutralTradeSession,
): SignalTradeDraft | null {
    const key = session.selectedDraftKey;
    if (null == key) return null;
    if ('FORWARD' === key) return (session as AiTradeSession).forwardDraft ?? null;
    if ('REVERSE' === key) return (session as AiTradeSession).reverseDraft ?? null;
    if ('MANUAL_LONG' === key) return (session as NeutralTradeSession).manualLongDraft ?? null;
    if ('MANUAL_SHORT' === key) return (session as NeutralTradeSession).manualShortDraft ?? null;
    return null;
}

/**
 * 为多个信号key生成AI交易计划叠加层。
 * 每个有有效AI分析的key都会生成一个AiTradePlanOverlay。
 */
export function selectActiveTradePlans(
    state: PatternPageAiState,
    selectedKeys: import('../types').AnalysisKey[],
    intervalMs: number = 3600000,
    signalFormationTimes: Map<string, number> = new Map(),
): AiTradePlanOverlay[] {
    if (!state.capabilities.overlayEnabled || !state.overlayVisible) return [];
    const plans: AiTradePlanOverlay[] = [];
    for (const key of selectedKeys) {
        const analysis = state.analysisByKey[key];
        if (null == analysis) continue;
        const formationTime = signalFormationTimes.get(key) ?? analysis.analysisGeneratedAt;
        const plan = analysisToDefaultOverlay(key, analysis, intervalMs, formationTime);
        if (plan) plans.push(plan);
    }
    return plans;
}

export function selectActiveTradePlan(
    state: PatternPageAiState,
    intervalMs: number = 3600000,
    formationTime: number | null = null,
): AiTradePlanOverlay | null {
    if (!state.capabilities.overlayEnabled
        || !state.overlayVisible || null === state.expandedAnalysisKey) {
        return null;
    }

    const analysis = state.analysisByKey[state.expandedAnalysisKey];
    if (null == analysis) return null;
    const signalStart = formationTime ?? analysis.analysisGeneratedAt;
    const hasValidHoldingBars = null != analysis.expectedHoldingBars
        && Number.isFinite(analysis.expectedHoldingBars)
        && analysis.expectedHoldingBars > 0;
    if (!Number.isFinite(analysis.analysisGeneratedAt) || 0 >= analysis.analysisGeneratedAt
        || (!hasValidHoldingBars && null != analysis.expectedHoldingUntil
            && analysis.expectedHoldingUntil <= analysis.analysisGeneratedAt)) return null;

    const displayCandles = resolveDisplayCandles(analysis, intervalMs);
    const displayEndTime = signalStart + intervalMs * displayCandles;
    const session = state.tradeSession;
    if (state.tradePanelOpen && session && session.analysisKey === state.expandedAnalysisKey
        && session.aiAnalysisId === analysis.id && session.selectedDraftKey) {
        const draft = selectSessionDraft(session);
        if (null == draft) return null;
        if (!validatePriceRelation(
            draft.posSide, draft.entryPrice, draft.targetPrice, draft.stopLossPrice,
        )) return null;

        const isForward = 'FORWARD' === session.selectedDraftKey;
        return {
            analysisKey: state.expandedAnalysisKey,
            direction: draft.posSide,
            startTime: signalStart,
            endTime: displayEndTime,
            entryPrice: draft.entryPrice,
            targetPrice: draft.targetPrice,
            stopLossPrice: draft.stopLossPrice,
            expectedHoldingBars: displayCandles,
            holdingRangeAvailable: true,
            profitLossRatio: calcProfitLossRatio(draft.entryPrice, draft.targetPrice, draft.stopLossPrice),
            expired: false,
            label: isForward ? 'AI正向'
                : 'REVERSE' === session.selectedDraftKey ? '反向观点'
                    : '手动方向',
        };
    }

    return analysisToDefaultOverlay(state.expandedAnalysisKey, analysis, intervalMs, signalStart);
}

function analysisToDefaultOverlay(
    key: import('../types').AnalysisKey,
    analysis: import('../types').AIAnalysis,
    intervalMs: number = 3600000,
    signalStart: number,
): AiTradePlanOverlay | null {
    const authoritativeSide = toPosSide(analysis.signalDirection);
    const entryPrice = analysis.entryPrice;
    const targetPrice = analysis.targetPrice;
    const stopLossPrice = analysis.stopLossPrice;
    if (null == entryPrice || null == targetPrice || null == stopLossPrice
        || !Number.isFinite(entryPrice) || !Number.isFinite(targetPrice) || !Number.isFinite(stopLossPrice)
        || 0 >= entryPrice || 0 >= targetPrice || 0 >= stopLossPrice) return null;
    if (authoritativeSide && !validatePriceRelation(
        authoritativeSide, entryPrice, targetPrice, stopLossPrice,
    )) return null;
    if (null == authoritativeSide
        && !validatePriceRelation('long', entryPrice, targetPrice, stopLossPrice)
        && !validatePriceRelation('short', entryPrice, targetPrice, stopLossPrice)) {
        return null;
    }
    // 中性信号若价格关系隐含方向，用推断方向绘制背景填充，标签保持'AI参考'
    const direction: 'long' | 'short' | 'neutral' = authoritativeSide
        ?? (
            validatePriceRelation('long', entryPrice, targetPrice, stopLossPrice) ? ('long' as const)
                : validatePriceRelation('short', entryPrice, targetPrice, stopLossPrice) ? ('short' as const)
                    : 'neutral'
        );
    const displayCandles = resolveDisplayCandles(analysis, intervalMs);
    const displayEndTime = signalStart + intervalMs * displayCandles;

    return {
        analysisKey: key,
        direction,
        startTime: signalStart,
        endTime: displayEndTime,
        entryPrice,
        targetPrice,
        stopLossPrice,
        expectedHoldingBars: displayCandles,
        holdingRangeAvailable: true,
        profitLossRatio: calcProfitLossRatio(entryPrice, targetPrice, stopLossPrice),
        expired: false,
        label: null == authoritativeSide ? 'AI参考' : 'AI正向',
    };
}

/**
 * 解析 plan 区域宽度（K 线根数）。
 * 业务定义：区域从信号形成 K 线开始，宽度 = 预计持仓时长 / 时间帧。
 * expectedHoldingBars 是机器绘图的权威依据；兼容旧数据时，由
 * expectedHoldingUntil - analysisGeneratedAt 还原持仓时长，避免把“信号形成到 AI 生成”的延迟算入区域。
 * 缺失/非法时回退到默认 15 根。
 */
function resolveDisplayCandles(
    analysis: import('../types').AIAnalysis,
    intervalMs: number,
): number {
    if (null != analysis.expectedHoldingBars
        && Number.isFinite(analysis.expectedHoldingBars)
        && analysis.expectedHoldingBars > 0) {
        return Math.round(analysis.expectedHoldingBars);
    }
    const expectedHoldingMs = null != analysis.expectedHoldingUntil
        ? analysis.expectedHoldingUntil - analysis.analysisGeneratedAt
        : null;
    const barsFromUntil = null != expectedHoldingMs && expectedHoldingMs > 0
        ? Math.round(expectedHoldingMs / intervalMs)
        : null;
    return barsFromUntil ?? 15;
}

function calcProfitLossRatio(
    entry: number,
    target: number,
    stopLoss: number,
): number {
    const upside = Math.abs(target - entry);
    const downside = Math.abs(entry - stopLoss);
    if (0 === downside || 0 === upside) return 0;
    return Math.round((upside / downside) * 100) / 100;
}
